import React from 'react';
import { Tag, Toggletip, ToggletipButton, ToggletipContent } from '@carbon/react';
import Information from '@carbon/icons-react/es/Information.js';
import { thisHostAndBasePath } from '@/../config';
import { DynamicTagProps } from '@/app/types';
import { validateBackslash } from '@/utils/urls';
import './tag.scss';
import { Link as CarbonLink} from '@carbon/react';

interface LegendPillProps {
  display: boolean;
  color: string;
  label: string;
}

const LegendPill: React.FC<LegendPillProps> = ({ display, color, label }) => {
  if (!display) return null;
  return (
    <span className={`legend-pill legend-pill--${color}`}>{label}</span>
  );
};

type CarbonTagColor =
  | 'red'
  | 'blue'
  | 'green'
  | 'magenta'
  | 'purple'
  | 'teal'
  | 'gray'
  | 'cyan'
  | 'cool-gray'
  | 'warm-gray'
  | 'outline';

interface ExtendedDynamicTagProps extends DynamicTagProps {
  showTooltip?: boolean;
  disableLinks?: boolean;
  tooltipOnly?: boolean;
}

export default function DynamicTag({
  ariaLabel,
  contentType,
  type = 'gray',
  categories,
  taxonomies = {},
  showTooltip = false,
  disableLinks = false,
  tooltipOnly = false,
}: ExtendedDynamicTagProps) {
  if (
    !categories ||
    (Array.isArray(categories) && categories.length === 0) ||
    contentType === 'courses'
  ) {
    return null;
  }
  const categoriesArray = Array.isArray(categories) ? categories : [categories];
  // const excludeTypes = ['feeds', 'tracking'];

  // const categoriesToDisplay = categoriesArray.filter((slug, index, arr) => {
  //   const taxonomy = taxonomies[slug];
  //   if (!taxonomy) return true;
  //   return arr.indexOf(slug) === index && !excludeTypes.includes(taxonomy.type || '');
  // });
  // const categoriesToDisplay = categoriesArray.filter(
  //   (slug, index, arr) => arr.indexOf(slug) === index,
  // );
  const categoriesToDisplay = categoriesArray.filter((slug, index, arr) => {
    const taxonomy = taxonomies[slug];
    const type = taxonomy?.type;
    // Only keep slugs with a defined type and type not 'taxonomy'
    return arr.indexOf(slug) === index && type && type.toLowerCase() !== 'taxonomy';
  });

  const getTagColor = (slug: string): CarbonTagColor => {
    const taxonomy = taxonomies[slug];
    if (!taxonomy) return type as CarbonTagColor;

    switch (taxonomy.type) {
      case 'technologies':
        return 'blue';
      case 'depmodels':
        return 'cyan';
      case 'components':
      case 'component':
        return 'teal';
      case 'languages':
        return 'gray';
      case 'devpractices':
        return 'magenta';
      case 'solutions':
        return 'red';
      case 'industries':
        return 'purple';
      case 'model_asset_technologies':
        return 'green';
      default:
        return 'gray';
    }
  };

  const displayNameLookup = (slug: string) => taxonomies[slug]?.name || slug;

  const linkLookup = (slug: string) => {
    const taxonomy = taxonomies[slug];
    const parentSlug =
      taxonomy?.template === 'component' ? 'components' : taxonomy?.type || 'taxonomy';
    return validateBackslash(`${thisHostAndBasePath}/${parentSlug}/${slug}/?cm_sp=ibmdev-_-developer-_-categorybutton`);
  };

  const listOfTypes = categoriesToDisplay.map(slug => taxonomies[slug]?.type || '').filter(Boolean);
  return (
    <div className="dynamic-tag">
      {showTooltip && (
        <div className="tooltipWrapper">
          <Toggletip align="top">
            <ToggletipButton label="Legend">
              <Information size={14} aria-label="Legend info" />
              <span className="visually-hidden">Legend</span>
            </ToggletipButton>
            <ToggletipContent>
              <div className="legend-pills">
                <LegendPill
                  color="cyan"
                  label="Architectures & Deployment"
                  display={listOfTypes.includes('depmodels')}
                />
                <LegendPill
                  color="magenta"
                  label="Development Practices"
                  display={listOfTypes.includes('devpractices')}
                />
                <LegendPill
                  color="gray"
                  label="Languages, frameworks, and runtimes"
                  display={listOfTypes.includes('languages')}
                />
                <LegendPill
                  color="teal"
                  label="Product & Services"
                  display={listOfTypes.includes('components')}
                />
                <LegendPill
                  color="blue"
                  label="Technologies"
                  display={listOfTypes.includes('technologies')}
                />
                <LegendPill
                  color="red"
                  label="Solutions"
                  display={listOfTypes.includes('solutions')}
                />
                <LegendPill
                  color="purple"
                  label="Industries"
                  display={listOfTypes.includes('industries')}
                />
                <LegendPill
                  color="green"
                  label="Model Asset Technologies"
                  display={listOfTypes.includes('model_asset_technologies')}
                />
              </div>
            </ToggletipContent>
          </Toggletip>
        </div>
      )}

      {!tooltipOnly && <nav aria-label="Categories" className="tag-sidenav">
        {categoriesToDisplay.map(slug => {
          const TagContent = (
            <Tag
              className="categories"
              type={getTagColor(slug)}
              aria-label={ariaLabel || taxonomies[slug]?.name}
            >
              {displayNameLookup(slug)}
            </Tag>
          );

          return disableLinks ? (
            <span key={slug} className="clickable">
              {TagContent}
            </span>
          ) : (
            <CarbonLink href={linkLookup(slug)} key={slug} className="clickable">
              {TagContent}
            </CarbonLink>
          );
        })}
      </nav>}
    </div>
  );
}
